import { useSyncExternalStore } from 'react'

/**
 * Store mínimo do encaixe do painel da IA (P4 §2.2, D1): o `InternalLayout` lê daqui a largura para
 * empurrar header e conteúdo (e recolher a sidebar sem gravar a preferência da pessoa). Muda só ao abrir,
 * fechar ou redimensionar — nunca por evento de IA — então a página não re-renderiza com o painel vivo.
 * Em repouso (`open:false, widthPx:0`) o layout se comporta exatamente como antes.
 */

export type TAiDockState = { open: boolean; mode: 'push' | 'sheet' | 'mobile'; widthPx: number; forceSidebarCollapsed: boolean }

export const AI_DOCK_REST: TAiDockState = Object.freeze({ open: false, mode: 'push', widthPx: 0, forceSidebarCollapsed: false })

let state: TAiDockState = AI_DOCK_REST
const listeners = new Set<() => void>()

export function getAiDockState(): TAiDockState {
  return state
}

export function setAiDockState(next: TAiDockState): void {
  if (
    next.open === state.open &&
    next.mode === state.mode &&
    next.widthPx === state.widthPx &&
    next.forceSidebarCollapsed === state.forceSidebarCollapsed
  ) {
    return
  }
  state = next
  if (typeof document !== 'undefined') {
    const pushed = next.open && next.mode === 'push' ? `${next.widthPx}px` : '0px'
    document.documentElement.style.setProperty('--ai-dock-w', pushed)
  }
  for (const listener of listeners) listener()
}

export function resetAiDock(): void {
  setAiDockState(AI_DOCK_REST)
  if (typeof document !== 'undefined') document.documentElement.style.removeProperty('--ai-dock-w')
}

let pendingReset: ReturnType<typeof setTimeout> | null = null

/**
 * Volta ao repouso quando o painel SAI da página — adiado um tique e cancelado se ele montar de novo. Reset
 * síncrono no unmount fechava o painel que a pessoa acabara de abrir quando o painel remontava (StrictMode,
 * troca de ramo durante o carregamento): o 1º clique em "IA" logo depois de carregar "não abria".
 */
export function scheduleAiDockReset(): void {
  if (pendingReset) clearTimeout(pendingReset)
  pendingReset = setTimeout(() => {
    pendingReset = null
    resetAiDock()
  }, 0)
}

export function cancelAiDockReset(): void {
  if (pendingReset) clearTimeout(pendingReset)
  pendingReset = null
}

/**
 * Aberto ou fechado ao medir o encaixe: antes da 1ª medida (`widthPx` 0) vale a escolha guardada da pessoa
 * (o botão grava a escolha ANTES de mudar o estado, então um clique antes da medida já está guardado); sem
 * escolha guardada, abre só se cabe empurrando sem recolher. Depois da 1ª medida, o estado atual manda.
 * Lê o estado ATUAL do store, nunca o de um render anterior (era isso que desfazia o clique).
 */
export function dockOpenOnMeasure(current: TAiDockState, stored: boolean | null, fitsPush: boolean): boolean {
  if (current.widthPx > 0) return current.open
  return stored ?? (current.open || fitsPush)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useAiDock(): TAiDockState {
  return useSyncExternalStore(subscribe, getAiDockState, () => AI_DOCK_REST)
}
